import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';

import * as helpers from '../../utils/utils.js';
import styles from "./Expenses.module.css";

import ReactLoading from 'react-loading';
import ErrorMessage from '../../components/shared/ErrorMessage.js';
import EditModal from '../../components/editModal/editModal.js';
import ExpenseForm from '../../components/forms/expenseForm.js';
import DateFilter from '../../components/dateFilter/dateFilter.js';

import { useExpenses } from '../../hooks/useExpenses.js';
import { useModal } from '../../hooks/useModal.js';


const Expenses = () => {
  const navigate = useNavigate();
  const { retrieveExpenses, updateExpense, createExpense, deleteExpense } = useExpenses();


  const { show: showExpense, close: hideExpense, open: openExpense } = useModal();
  const [ selectedExpenseModalData, setSelectedExpenseModalData ] = useState(null);
  const [ filter, setFilter ] = useState({ year: "", month: "", day: "" });

  const {
    data: expenses,
    error: expensesError,
    isLoading: expensesAreLoading,
  } = useQuery(["expenseData"], () => retrieveExpenses(), {
    staleTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    cacheTime: 1000 * 60 * 60,
  });

  const handleSubmitExpense = async ({ data, key }) => {
    console.log("Updating expense:", key, data);

    if (key == null) {
      // Creating new expense
      try {
        const result = await createExpense({ ...data });
        console.log('expense created:', result);
      } catch (error) {
        console.error('Error creating expense:', error);
      }
      setSelectedExpenseModalData(null);
      return;
    }

    // Updating existing expense
    try {
      const result = await updateExpense({
        id: key, 
        expenseData: { ...data }
      });
      console.log('expense updated:', result);
    } catch (error) {
      console.error('Error updating expense:', error);
    }

    setSelectedExpenseModalData(null);
  }

  const handleDeleteExpense = async (id) => {
    
    try {
      await deleteExpense(id);
      console.log('Expense deleted:', id);
    } catch (error) {
      console.error('Error deleting expense:', error);
    }

    setSelectedExpenseModalData(null);
  };

  if (expensesAreLoading) return (
    <div className="centering-container">
      <ReactLoading type="spin" color="#000" />
    </div>
    );

  if (expensesError) {
    console.error("Error fetching expenses:", expensesError);
    return <ErrorMessage 
      title="Connection Issue"
      error={"There seems to be an issue. Please try again later."}
    />;
  }

  // Filter by the selected year / month / day (dates are UTC-midnight ms).
  const matchesDate = (expense) => {
    if (!filter.year && !filter.month && !filter.day) return true;
    if (expense.date == null) return false;
    const d = new Date(Number(expense.date));
    if (filter.year && d.getUTCFullYear() !== Number(filter.year)) return false;
    if (filter.month && d.getUTCMonth() + 1 !== Number(filter.month)) return false;
    if (filter.day && d.getUTCDate() !== Number(filter.day)) return false;
    return true;
  };

  const filteredExpenses = expenses.filter(matchesDate);

  const groupedExpenses = filteredExpenses.reduce((acc, expense) => {
    if (!acc[expense.tag]) acc[expense.tag] = [];
    acc[expense.tag].push(expense);
    return acc;
  }, {});

  const groupedExpenseTotals = {};
  for (const tag in groupedExpenses) {
    groupedExpenseTotals[tag] = groupedExpenses[tag].reduce((sum, expense) => sum + expense.amount, 0);
  }

  return (
    <div className="m-3">
      
      <h2>Expenses</h2>
      <div className="d-flex flex-wrap align-items-end justify-content-between gap-3 mb-3">
        <DateFilter value={filter} onChange={setFilter} />
        <button
          className="btn btn-primary"
          onClick={() => {
            setSelectedExpenseModalData();
            openExpense();
          }}
        >
          Add New Expense
        </button>
      </div>
      <table className={`table ${styles.expenseTable}`}>
        <thead>
          <tr>
            <th>Date</th>
            <th>Category</th>
            <th>Amount</th>
            <th>Description</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {Object.keys(groupedExpenses).map(tag => {
            return (
      
            <React.Fragment key={tag}>
              {/* Section Header */}
              <tr className="table-secondary">
                <td colSpan={5} style={{ fontWeight: "bold" }}>
                  {tag.toUpperCase()}
                </td>
              </tr>

              {/* Rows for this tag */}
              {groupedExpenses[tag].map(expense => {
                return (
                  <tr key={expense.id}>
                    <td>{helpers.formatDate(expense.date)}</td>
                    <td>{expense.tag}</td>
                    <td>{expense.amount.toFixed(2)}</td>
                    <td>{expense.description}</td>
                    <td>
                      <button 
                        className="btn btn-sm btn-danger"
                        onClick={() => {
                          setSelectedExpenseModalData({ ...expense, id: expense.id });
                          openExpense();
                        }}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                );
              })}
              <tr className="table-info" key={tag}>
                <td colSpan="2"><strong>{tag[0].toUpperCase() + tag.slice(1)} Total</strong></td>
                <td><strong>{groupedExpenseTotals[tag].toFixed(2)}</strong></td>
                <td colSpan="2"></td>
              </tr>
              
            </React.Fragment>
          );
        })}

        </tbody>
        <tfoot>
          {Object.keys(groupedExpenseTotals).map(tag => {
            return (
              <tr className="table-info" key={tag}>
                <td colSpan="2"><strong>{tag[0].toUpperCase() + tag.slice(1)} Total</strong></td>
                <td><strong>{groupedExpenseTotals[tag].toFixed(2)}</strong></td>
                <td colSpan="2"></td>
              </tr>
            );
          })}
          <tr>
            <td colSpan="2"><strong>Total</strong></td>
            <td><strong>{filteredExpenses.reduce((sum, expense) => sum + expense.amount, 0).toFixed(2)}</strong></td>
            <td colSpan="2"></td>
          </tr>
        </tfoot>
      </table>

      <EditModal title="Expense" show={showExpense} onClose={() => hideExpense()}>
        <ExpenseForm initialData={selectedExpenseModalData} dataKey={selectedExpenseModalData?.id}
          onSubmit={(newData) => {
            console.log("Submitting expense data:", newData);
            const { id, ...expenseData } = newData;
            hideExpense();
            handleSubmitExpense({ data: {...expenseData}, key:id });
          }}
          
          onDelete={(key) => {
            hideExpense();
          handleDeleteExpense(key);
          }}
          />
      </EditModal>  
    </div>

    
  );
};

export default Expenses;
